import { useEffect, useRef, useState } from 'react';
import { APIProvider, Map, AdvancedMarker, useMap } from '@vis.gl/react-google-maps';
import { Capacitor } from '@capacitor/core';
import type { NearbyUser } from "@/hooks/useNearbyUsers";
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { MapPin, Gift, Clock, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { calculateDistance, formatDistance } from '@/lib/distance';
import { formatLastSeen, isOnline } from '@/lib/time';
import { SaveUserButton } from './SaveUserButton';

// Default center (San Francisco) if no center is provided
const DEFAULT_CENTER = { lat: 37.7749, lng: -122.4194 };

interface NearbyUsersMapProps {
  users: NearbyUser[];
  selectedUser: NearbyUser | null;
  onUserSelect: (user: NearbyUser) => void;
  defaultCenter?: { lat: number; lng: number } | null;
  currentUserId?: string | null;
  recenterTrigger?: number;
  recenterTarget?: { lat: number; lng: number } | null;
  currentUserLocation?: { lat: number; lng: number } | null;
  onSendGift?: (userId: string) => void;
  onBoundsChanged?: (bounds: { north: number; south: number; east: number; west: number }, zoom: number) => void;
}

/**
 * Inner component that handles map recentering logic and marker rendering
 */
const MapContent = ({ users, selectedUser, onUserSelect, currentUserId, recenterTrigger, recenterTarget, currentUserLocation, onSendGift, onBoundsChanged }: Omit<NearbyUsersMapProps, 'defaultCenter'>) => {
  const map = useMap();
  const navigate = useNavigate();
  const prevTrigger = useRef(recenterTrigger);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const boundsInitRef = useRef(false);

  useEffect(() => {
    if (!map) return;
    // Fire once on mount if bounds are available
    if (!boundsInitRef.current) {
      const b = map.getBounds?.();
      if (b) {
        const ne = b.getNorthEast();
        const sw = b.getSouthWest();
        onBoundsChanged?.({ north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() }, map.getZoom?.() ?? 0);
        boundsInitRef.current = true;
      }
    }
    // Listen to idle (pan/zoom end)
    const idleListener = map.addListener?.('idle', () => {
      const b = map.getBounds?.();
      if (!b) return;
      const ne = b.getNorthEast();
      const sw = b.getSouthWest();
      const z = map.getZoom?.() ?? 0;
      onBoundsChanged?.({ north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() }, z);
    });
    return () => {
      // @types for google maps listeners have remove()
      try { (idleListener as any)?.remove?.(); } catch {}
    };
  }, [map, onBoundsChanged]);

  useEffect(() => {
    if (map && recenterTrigger !== prevTrigger.current && recenterTarget) {
      map.panTo(recenterTarget);
      prevTrigger.current = recenterTrigger;
    }
  }, [map, recenterTrigger, recenterTarget]);

  return (
    <>
      {users.map((user) => {
        // Only render users that have a valid latitude and longitude
        if (user.lat == null || user.lng == null) {
          return null;
        }

        const isSelected = !!selectedUser && selectedUser.id === user.id;
        const isCurrentUser = !!currentUserId && currentUserId === user.id;
        const position = { lat: user.lat, lng: user.lng };
        const isOpen = openPopoverId === user.id;

        // Calculate distance if current user location is available
        const distance = currentUserLocation && user.lat != null && user.lng != null
          ? calculateDistance(currentUserLocation.lat, currentUserLocation.lng, user.lat, user.lng)
          : null;

        const userIsOnline = isOnline(user.location_updated_at);

        return (
          <AdvancedMarker key={user.id} position={position}>
            <Popover open={isOpen} onOpenChange={(open) => {
              setOpenPopoverId(open ? user.id : null);
              if (open) onUserSelect(user);
            }}>
              <PopoverTrigger asChild>
                <div
                  className={`relative transition-transform cursor-pointer ${
                    isSelected ? 'scale-125' : 'scale-100'
                  }`}
                >
                  {/* Pulse ring for the signed-in/current user */}
                  {isCurrentUser && <span className="pulse-ring" aria-hidden />}

                  <Avatar className={`relative z-10 w-12 h-12 border-2 ${isCurrentUser ? 'border-accent' : 'border-white'} shadow-warm`}>
                    <AvatarImage src={user.avatar_url || undefined} />
                    <AvatarFallback>
                      {user.name ? user.name[0] : '?'}
                    </AvatarFallback>
                  </Avatar>
                  <MapPin className="absolute -bottom-1 -right-1 w-4 h-4 text-primary fill-white" />
                </div>
              </PopoverTrigger>
              {isOpen && (
                <PopoverContent 
                  className="w-80 bg-background border-border shadow-lg z-[100]"
                  align="center"
                  side="top"
                  sideOffset={10}
                >
                  <div className="space-y-4">
                    {/* User Info */}
                    <div className="flex items-start gap-3">
                      <Avatar className="w-16 h-16 border-2 border-border">
                        <AvatarImage src={user.avatar_url || undefined} />
                        <AvatarFallback>{user.name ? user.name[0] : '?'}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg text-foreground truncate">
                          {user.name || 'User'}
                        </h3>
                        {user.status && (
                          <p className="text-sm text-muted-foreground italic mt-1 line-clamp-2">
                            "{user.status}"
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Status and Distance */}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span>{userIsOnline ? 'Online' : formatLastSeen(user.location_updated_at)}</span>
                      </div>
                      {distance !== null && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{formatDistance(distance)}</span>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    {!isCurrentUser && (
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          {user.name?.toLowerCase().startsWith('ambassador') && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => {
                                navigate(`/prize-draw?user=${user.id}`);
                                setOpenPopoverId(null);
                              }}
                            >
                              <Ticket className="w-4 h-4 mr-1" />
                              Draw
                            </Button>
                          )}
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={() => {
                              if (onSendGift) onSendGift(user.id);
                              setOpenPopoverId(null);
                            }}
                          >
                            <Gift className="w-4 h-4 mr-1" />
                            Gift
                          </Button>
                          <SaveUserButton userId={user.id} userName={user.name || 'user'} size="sm" />
                        </div>
                      </div>
                    )}
                  </div>
                </PopoverContent>
              )}
            </Popover>
          </AdvancedMarker>
        );
      })}
    </>
  );
};

/**
 * A basic Google Map component that displays user markers.
 * The map is uncontrolled to allow free panning and zooming.
 */
const NearbyUsersMap = ({ users, selectedUser, onUserSelect, defaultCenter, currentUserId, recenterTrigger, recenterTarget, currentUserLocation, onSendGift }: NearbyUsersMapProps) => {
  const isNative = Capacitor.isNativePlatform?.() ?? (Capacitor.getPlatform?.() !== 'web');
  const apiKey = (isNative ? (import.meta.env.VITE_GOOGLE_MAPS_API_KEY_NATIVE as string | undefined) : undefined) || (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string);
  return (
    <APIProvider apiKey={apiKey} authReferrerPolicy="origin">
      <Map
        defaultCenter={defaultCenter || DEFAULT_CENTER}
        defaultZoom={defaultCenter ? 14 : 12}
        mapId={import.meta.env.VITE_GOOGLE_MAP_ID || undefined}
        disableDefaultUI={false}
        zoomControl={true}
        gestureHandling={'greedy'}
        clickableIcons={false}
        className="w-full h-full rounded-lg"
      >
        <MapContent
          users={users}
          selectedUser={selectedUser}
          onUserSelect={onUserSelect}
          currentUserId={currentUserId}
          recenterTrigger={recenterTrigger}
          recenterTarget={recenterTarget}
          currentUserLocation={currentUserLocation}
          onSendGift={onSendGift}
        />
      </Map>
    </APIProvider>
  );
};

export default NearbyUsersMap;
